import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import Uppy from '@uppy/core'
import AwsS3 from '@uppy/aws-s3'

import { useThunkDispatch } from '@domain-group/react-hooks'

import * as attachmentActions from '@redux/modules/attachment'

import type { AttachmentModel } from '@models/attachment'
import type { AppDispatch } from '@redux/store'

import '@uppy/core/dist/style.css'
import '@uppy/drag-drop/dist/style.css'

type UseUppyAttachmentUploadOptions = {
  attachmentParams: object,
  successCallback: () => void,
  uppyOptions: object,
}

const initializeUppy = (options: UseUppyAttachmentUploadOptions, dispatch: AppDispatch) => {
  const { attachmentParams, successCallback, uppyOptions = {} } = options
  const { createAttachment, deleteAttachment, updateAttachment } = attachmentActions

  const uppy = new Uppy({
    debug: true,
    autoProceed: true,
    ...uppyOptions,
  })
    .use(AwsS3, {
      // @ts-expect-error TS2322
      getUploadParameters(file) {
        const attachment: AttachmentModel = {
          ...attachmentParams,
          content_type: file.type,
          file_name: file.name,
          title: file.name,
        }

        return dispatch(createAttachment(attachment, { presign_url_only: true }))
          // @ts-expect-error TS2345
          .then(({ data }: { data: AttachmentModel }) => {
            const { data: attachmentData, id } = data || {}
            const { presigned_url } = attachmentData || {}

            // Store the attachment ID in meta data so we can update later
            uppy.setFileMeta(file.id, { attachmentId: id })

            return {
              method: 'PUT',
              url: presigned_url,
              fields: [],
            }
          })
      },
    })
    .on('file-removed', (file) => {
      const {
        meta,
        progress,
      } = file

      const { attachmentId } = meta

      const { uploadStarted, uploadComplete } = progress || {}

      if (uploadStarted && attachmentId && !uploadComplete){
        dispatch(deleteAttachment({ id: Number(attachmentId) }))
      }
    })
    .on('upload-error', (file) => {
      const {
        meta: { attachmentId },
      } = file

      if (attachmentId){
        dispatch(deleteAttachment({ id: Number(attachmentId) }))
      }
    })
    .on('upload-success', (file) => {
      const {
        meta: { attachmentId },
        name,
        size,
        type,
      } = file

      if (attachmentId){
        const updatedAttachment: AttachmentModel = {
          id: attachmentId,
          file_file_name: name,
          file_content_type: type,
          // @ts-expect-error TS2353
          file_file_size: size,
        }

        // @ts-expect-error TS2345
        dispatch(updateAttachment(updatedAttachment, { include_attachment_file: true })).then(({ success }) => {
          if (!success){
            toast.warning(`Upload ${name} Failed`)
            dispatch(deleteAttachment({ id: attachmentId }))
            return
          }

          if (successCallback) successCallback()
        })
      }
    })

  return uppy
}

const checkRequiredOptions = (options = {}) => {
  const { attachmentParams }: {attachmentParams?: Partial<AttachmentModel>} = options

  const requiredOptions = ['attachable_type', 'attachable_id']
  // @ts-expect-error TS2769
  const optionKeys = Object.keys(attachmentParams)

  return requiredOptions.every(key => optionKeys.includes(key))
}

function useUppyAttachmentUpload(options: UseUppyAttachmentUploadOptions, deps: string[] = []) {
  const [uppy, setUppy] = useState<Uppy | undefined>(undefined)

  const dispatch = useThunkDispatch()

  useEffect(() => {
    if (checkRequiredOptions(options)){
      return setUppy(initializeUppy(options, dispatch))
    }

    return () => {
      if (uppy){
        uppy.close()
        setUppy(undefined)
      }
    }
  }, [...deps])

  return uppy
}

export default useUppyAttachmentUpload
